import {
    DocumentTypesEnum,
    RestrictedDocumentCategory_CaseExpertReport,
} from '../../utilities/Constants';
import { Dropdown, OverlayTrigger, Table, Tooltip, TooltipProps } from 'react-bootstrap';
import { Help, IHelpProps } from '../shared/Help';
import { ICheckmateTaskModel, IDocumentModel } from '../../interfaces/ICase';
import { showErrorToast, showSuccessToast } from '../../shared/ToastUtils';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import { CheckmateDialog } from '../shared/dialog';
import Common from '../../stores/Common';
import { ContextMenu } from '../../shared/context-menu/ContextMenu';
import DocumentEditModal from '../shared/document-upload/DocumentEditModal';
import DocumentUploadModal from '../shared/document-upload/DocumentUploadModal';
import { DocumentVersionsList } from './DocumentVersionsList';
import { DownloadIconButton } from '../shared/download-button/DownloadIconButton';
import { ILookupModel } from '../../interfaces/ILookup';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { IconButton } from '../../shared/buttons/IconButton';
import { Link } from 'react-router-dom';
import { LinkToExternalDocumentModal } from '../shared/external-documents-modal/LinkToExternalDocumentModal';
import { Loader } from '../shared/Loader';
import Sort from '../../stores/Sort';
import { SortableHeader } from '../shared/SortableHeader';
import { Stack } from 'react-bootstrap';
import ValidateUtils from '../../shared/validations';
import { cloneDeep } from 'lodash';
import { useDidUpdate } from '@mantine/hooks';
import { useDocumentCategoryTypes } from '../../shared/react-query-hooks/useDocumentCategoryTypes';
import { useDocumentDownload } from '../../shared/react-query-hooks/useDocumentDownload';
import { usePanelContext } from '../side-panel/PanelContext';
import { useState } from 'react';
import { useZoneContext } from '../../contexts/useZoneContext';

export interface IDocumentsListProps {
    authorizedToEdit: boolean;
    cannotViewOrDownloadDocument?: boolean;
    categoryDocumentTypes?: ILookupModel[];
    disableUploadButton?: boolean;
    displayUploadDocumentDialog?: boolean;
    documents?: IDocumentModel[];
    documentTypeId: number;
    documentSubTypeId?: number | undefined;
    entityGuid: string;
    entityTypeId: number;
    helpProps?: IHelpProps;
    keepDocumentUploadDialogOpen?: boolean; // AR - 12/27/2023 - As per conv with Josh, these 2 are possible dups. Will revisit when this component is refactored.
    noteTypeId?: number;
    onCreateEntityBeforeUpload?: (
        keepDocumentUploadDialogOpen?: boolean,
        stayInEditMode?: boolean
    ) => void; // This is for a scenario where the parent entity for the document doesnt exist. Hence the parent will be responsible for creating the entity first and then the document.
    onDeleteComplete?: (documentGuid: string) => void;
    onSaveComplete?: (documents: IDocumentModel[]) => void;
    parentEntityJson?: string;
    parentEntityRedirectUrl?: string;
    showHelpButton?: boolean;
    strategyTypeId?: number;
    tasks?: ICheckmateTaskModel[];
    title?: string | React.ReactElement;
    uploadOnly?: boolean;
    user: IUserModel;
}

export function DocumentsList(props: IDocumentsListProps) {
    const zoneContext = useZoneContext();
    const { openPanel } = usePanelContext();

    const { downloadAsync } = useDocumentDownload({
        endpoint: '/' + ApiRoutes.EntityDocumentDownload,
        onError: (err) => showErrorToast(err.message),
    });

    const categoryDocumentTypesApi = useDocumentCategoryTypes(props.entityTypeId);

    const [currentDocument, setCurrentDocument] = useState<IDocumentModel | undefined>(undefined);
    const [documentGuidToDelete, setDocumentGuidToDelete] = useState<string>('');

    const [localDocuments, setLocalDocuments] = useState<IDocumentModel[]>(props.documents ?? []);
    const [documentsDownloading, setDocumentsDownloading] = useState<string[]>([]);
    const [documentsWithErrors, setDocumentsWithErrors] = useState<string[]>([]);
    const [pendingResponse, setPendingResponse] = useState<boolean>(false);
    const [showConfirmRemoveDialog, setShowConfirmRemoveDialog] = useState<boolean>(false);
    const [showDocumentEditModal, setShowDocumentEditModal] = useState<boolean>(false);
    const [showDocumentUploadModal, setShowDocumentUploadModal] = useState<boolean>(false);
    const [showLinkExternalDocumentModal, setShowLinkExternalDocumentModal] =
        useState<boolean>(false);
    const [openMenuGuid, setOpenMenuGuid] = useState<string | null>(null);

    const [validation, setValidation] = useState<IValidation>({} as IValidation);

    const getModifiedByName = (user?: IUserModel) => {
        if (user?.profile?.firstName && user.profile.lastName) {
            return `${user.profile.firstName} ${user.profile.lastName}`;
        }

        return '';
    };

    const sortData = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        let documentsCopy = cloneDeep(localDocuments);

        if (documentsCopy) {
            if (key == 'modifiedDate') {
                documentsCopy = documentsCopy.sort(Sort.compareDate(key, subKey, order));
            } else {
                documentsCopy = documentsCopy.sort(
                    Sort.compareValues(key, subKey, order, subGrandKey)
                );
            }
        }
        setLocalDocuments(documentsCopy);
    };

    const showConfirmRemoveDailog = (documentGuid: string) => {
        setShowConfirmRemoveDialog(true);
        setDocumentGuidToDelete(documentGuid);
    };

    const handleDeleteModalConfirm = async () => {
        setPendingResponse(true);

        const documentTitle = localDocuments.find(
            (doc) => doc.guid === documentGuidToDelete
        )?.fileName;

        const response = await zoneContext.zoneApiClient.deleteEntityDocument(
            props.entityGuid,
            props.entityTypeId,
            documentGuidToDelete,
            props.noteTypeId,
            props.strategyTypeId
        );

        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }

        setPendingResponse(false);
        setShowConfirmRemoveDialog(false);

        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            return;
        }

        if (props.onDeleteComplete) {
            showSuccessToast(
                <span>
                    <strong>{documentTitle}</strong> deleted successfully
                </span>
            );
            props.onDeleteComplete(documentGuidToDelete);
        } else {
            window.location.reload();
        }
    };

    const handleDeleteModalClose = () => {
        setShowConfirmRemoveDialog(false);
    };

    const handleUploadDocumentClick = () => {
        if (!props.entityGuid && props.onCreateEntityBeforeUpload) {
            props.onCreateEntityBeforeUpload(true);
        }
        setShowDocumentUploadModal(true);
    };

    const handleExternalDocumentLinkClick = () => {
        if (!props.entityGuid && props.onCreateEntityBeforeUpload) {
            props.onCreateEntityBeforeUpload(true);
        }
        setShowLinkExternalDocumentModal(true);
    };

    const handleEditDocumentClick = (document: IDocumentModel) => {
        setShowDocumentEditModal(true);
        setCurrentDocument(document);
    };

    const handleCloseLinkExternalDocumentClick = () => {
        setShowLinkExternalDocumentModal(false);
    };

    const handleCloseUploadDocumentClick = () => {
        setShowDocumentUploadModal(false);
    };

    const handleCloseEditDocumentClick = () => {
        setShowDocumentEditModal(false);
        setCurrentDocument(undefined);
    };

    const handleDocumentDownloadClick = async (
        documentToDownload: IDocumentModel,
        fileVersionGuid?: string
    ) => {
        const { guid } = documentToDownload;
        const fallbackFileVersionGuid =
            fileVersionGuid ?? documentToDownload.documentVersions?.[0]?.guid ?? guid!;

        const handleDownloadSuccess = () => {
            setDocumentsWithErrors((prev) => prev.filter((docGuid) => docGuid !== guid));
        };

        const handleDownloadError = () => {
            setDocumentsWithErrors((prev) => [...prev, guid!]);
        };

        setDocumentsDownloading((prev) => [...prev, fallbackFileVersionGuid]);

        await downloadAsync({
            entityGuid: props.entityGuid,
            entityTypeId: props.entityTypeId,
            documentVersionGuid: fallbackFileVersionGuid,
            noteTypeId: props.noteTypeId,
            strategyTypeId: props.strategyTypeId,
            tenantGuid: zoneContext.zoneGuid,
        })
            .then(handleDownloadSuccess)
            .catch(handleDownloadError)
            .finally(() => {
                setDocumentsDownloading((prev) =>
                    prev.filter((docGuid) => docGuid !== fallbackFileVersionGuid)
                );
            });
    };

    const handleSaveEditDocumentComplete = (document: IDocumentModel) => {
        setShowDocumentEditModal(false);
        props.onSaveComplete?.([document]);
    };

    const handleSaveMultipleComplete = (documents: IDocumentModel[]) => {
        setShowDocumentUploadModal(false);
        setShowLinkExternalDocumentModal(false);
        props.onSaveComplete?.(documents);
    };

    useDidUpdate(() => {
        setLocalDocuments(props.documents ?? []);
    }, [props.documents]);

    useDidUpdate(() => {
        if (props.keepDocumentUploadDialogOpen) {
            const newDocument: IDocumentModel = {
                documentTypeId: DocumentTypesEnum.WorkProduct.Value,
            };
            setShowDocumentUploadModal(true);
            setCurrentDocument(newDocument);
        }
    }, [props.keepDocumentUploadDialogOpen]);

    const categoryDocumentTypes = categoryDocumentTypesApi?.data ?? [];
    const showCategoryColumn = categoryDocumentTypes.length > 0;

    const renderTooltip = (props: TooltipProps) => (
        <Tooltip {...props}>
            <div style={{ whiteSpace: 'nowrap' }}>Invalid Note - Upload Disbaled</div>
        </Tooltip>
    );

    const uploadButton = (
        <IconButton
            buttonCssClass="btn btn-black"
            icon="fal fa-upload"
            onClick={handleUploadDocumentClick}
            disabled={props.disableUploadButton}
            text="Upload Documents"
        />
    );

    return (
        <div>
            {pendingResponse ? <Loader /> : null}
            <div>
                <span className="text-danger">{validation.model}</span>
            </div>

            <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                {typeof props.title === 'string' && (
                    <div className="text-gray margin-top-sm" style={{ display: 'inline-block' }}>
                        {props.title}
                    </div>
                )}
                {(typeof props.title !== 'string' && props.title) || <span></span>}

                <Stack direction="horizontal" gap={3}>
                    {props.authorizedToEdit && (
                        <>
                            <span className="link" onClick={handleExternalDocumentLinkClick}>
                                Link external documents
                            </span>
                            {!props.disableUploadButton ? (
                                <>{uploadButton}</>
                            ) : (
                                <OverlayTrigger
                                    placement="left"
                                    delay={{ show: 250, hide: 400 }}
                                    overlay={!props.showHelpButton ? renderTooltip : <></>}
                                >
                                    <span className="d-inline-block">
                                        {uploadButton}
                                        {props.showHelpButton && props.helpProps && (
                                            <Help
                                                type={props.helpProps.type}
                                                title={props.helpProps.title}
                                                helpText={props.helpProps.helpText}
                                            />
                                        )}
                                    </span>
                                </OverlayTrigger>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>

            <table className="table">
                <thead>
                    <tr style={{ verticalAlign: 'middle' }}>
                        <SortableHeader
                            headerText="File Name"
                            sortKey="fileName"
                            onSort={sortData}
                            noWrap
                        />
                        <SortableHeader
                            headerText="File Type"
                            sortKey="fileExtensionTypeId"
                            onSort={sortData}
                            noWrap
                        />
                        {showCategoryColumn && <th>Category</th>}
                        <SortableHeader
                            headerText="Modified by"
                            sortKey="modifiedBy"
                            subKey="profile"
                            subGrandKey="firstName"
                            onSort={sortData}
                            noWrap
                        />
                        <SortableHeader
                            headerText="Modified date"
                            sortKey="modifiedDate"
                            onSort={sortData}
                            noWrap
                        />
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {localDocuments?.map((document: IDocumentModel, index: number) => {
                        const isRestrictedDocument =
                            props.cannotViewOrDownloadDocument &&
                            document.documentCategoryTypeIds?.includes(
                                RestrictedDocumentCategory_CaseExpertReport
                            );

                        const downloadFailed = documentsWithErrors.some(
                            (guid) => guid === document.guid
                        );

                        const isExternalDocument = Boolean(document.externalURL);

                        return (
                            <tr key={index}>
                                <td>{document.fileName ? document.fileName : ''}</td>
                                <td>{document.fileExtensionTypeName}</td>
                                {showCategoryColumn && (
                                    <td>
                                        {document.documentCategoryTypeIds
                                            ?.map(
                                                (docCategoryTypeId) =>
                                                    props.categoryDocumentTypes?.find(
                                                        (docType) =>
                                                            docType.id === docCategoryTypeId
                                                    )?.displayName
                                            )
                                            .join(' | ')}
                                    </td>
                                )}
                                <td>{getModifiedByName(document.modifiedBy)}</td>
                                <td>{Common.dateTimeFormat(document.modifiedDate)}</td>
                                <td>
                                    <Stack
                                        direction="horizontal"
                                        gap={0}
                                        style={{
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {!isRestrictedDocument && !isExternalDocument && (
                                            <DownloadIconButton
                                                isDownloading={
                                                    documentsDownloading?.includes(
                                                        document.documentVersions?.[0]?.guid ?? ''
                                                    ) ?? false
                                                }
                                                isError={downloadFailed}
                                                onClick={() =>
                                                    handleDocumentDownloadClick(
                                                        document,
                                                        document.documentVersions?.[0]?.guid
                                                    )
                                                }
                                                document={document}
                                            />
                                        )}

                                        {isExternalDocument && (
                                            <Link
                                                to={document.externalURL!}
                                                target="_blank"
                                                className="btn btn-no-bg mr-0"
                                            >
                                                <i className="fal fa-lg fa-arrow-up-right-from-square"></i>
                                            </Link>
                                        )}

                                        <ContextMenu
                                            isOpen={openMenuGuid === document.guid}
                                            onToggle={(next) =>
                                                setOpenMenuGuid(
                                                    next && document.guid ? document.guid : null
                                                )
                                            }
                                        >
                                            <Dropdown.Item
                                                disabled={
                                                    !props.authorizedToEdit || isRestrictedDocument
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditDocumentClick(document);
                                                }}
                                            >
                                                <i className="fal fa-pen me-2" />
                                                Rename
                                            </Dropdown.Item>
                                            {(document.documentVersions?.length ?? 0) > 0 && (
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        openPanel({
                                                            id: 'document-versions',
                                                            title: 'Document Versions',
                                                            content: (
                                                                <DocumentVersionsList
                                                                    document={document}
                                                                    entityGuid={props.entityGuid}
                                                                    entityTypeId={props.entityTypeId}
                                                                    noteTypeId={props.noteTypeId}
                                                                    strategyTypeId={props.strategyTypeId}
                                                                />
                                                            ),
                                                            placement: 'end',
                                                            width: '350px',
                                                        });
                                                    }}
                                                >
                                                    <i className="fal fa-history me-2" />
                                                    Versions
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                                disabled={
                                                    !props.authorizedToEdit || isRestrictedDocument
                                                }
                                                onClick={() => {
                                                    showConfirmRemoveDailog(document.guid!);
                                                }}
                                            >
                                                <i className="fal fa-trash-alt me-2" />
                                                Delete
                                            </Dropdown.Item>
                                        </ContextMenu>
                                    </Stack>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {localDocuments.length === 0 && <div>No Attachments found!</div>}

            {showDocumentEditModal && currentDocument && (
                <DocumentEditModal
                    currentDocument={currentDocument}
                    entityTypeId={props.entityTypeId}
                    onHide={handleCloseEditDocumentClick}
                    onSaveComplete={handleSaveEditDocumentComplete}
                    open={!!showDocumentEditModal}
                />
            )}

            {showDocumentUploadModal && props.documentTypeId && (
                <DocumentUploadModal
                    documentTypeId={props?.documentTypeId}
                    documentSubTypeId={props?.documentSubTypeId}
                    entityGuid={props.entityGuid}
                    entityTypeId={props.entityTypeId}
                    existingDocuments={localDocuments}
                    noteTypeId={props.noteTypeId}
                    onHide={handleCloseUploadDocumentClick}
                    onSaveComplete={handleSaveMultipleComplete}
                    open={showDocumentUploadModal}
                />
            )}

            {showLinkExternalDocumentModal && props.documentTypeId && (
                <LinkToExternalDocumentModal
                    documentTypeId={props?.documentTypeId}
                    documentSubTypeId={props?.documentSubTypeId}
                    entityGuid={props.entityGuid}
                    entityTypeId={props.entityTypeId}
                    existingDocuments={localDocuments}
                    noteTypeId={props.noteTypeId}
                    onHide={handleCloseLinkExternalDocumentClick}
                    onSaveComplete={handleSaveMultipleComplete}
                    open
                    strategyTypeId={props.strategyTypeId}
                />
            )}

            <CheckmateDialog
                body="Are you sure you want to delete the selected item?"
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                confirmText="Delete"
                handleClose={handleDeleteModalClose}
                handleConfirm={handleDeleteModalConfirm}
                isShowingModal={showConfirmRemoveDialog}
                title="Delete"
                showBackdrop
            />
        </div>
    );
}
